/**
 * SeoSchema.tsx — GEO & AEO Structured Data
 * 
 * Contains all Schema.org structured data for:
 * - Service Schema (each major treatment)
 * - How-To Schema (popular treatments)
 * - Breadcrumb Schema
 * - Organization Schema (enhanced)
 * - Medical entity-rich data for AI engines
 */

export function SeoSchemaMarkup() {
  const schemas = [
    // === SERVICE SCHEMAS (GEO: entity-rich for AI citation) ===
    {
      "@context": "https://schema.org",
      "@type": "Service",
      "serviceType": "Hollywood Smile",
      "provider": {
        "@type": "MedicalBusiness",
        "name": "Bella Rose Medical Center",
        "address": {
          "@type": "PostalAddress",
          "streetAddress": "Muhammad Bin Rashid Charity Association Building, Cairo Street",
          "addressLocality": "Al Mamzar",
          "addressRegion": "Dubai",
          "addressCountry": "AE"
        }
      },
      "areaServed": { "@type": "City", "name": "Dubai" },
      "description": "Premium Hollywood Smile veneers using 3D and 6D porcelain technology. 5 specialist dentists, DHA-licensed clinic in Al Mamzar Dubai.",
      "offers": {
        "@type": "AggregateOffer",
        "lowPrice": "3000",
        "highPrice": "6000",
        "priceCurrency": "AED",
        "offerCount": "2"
      }
    },
    {
      "@context": "https://schema.org",
      "@type": "Service",
      "serviceType": "Botox Injection",
      "provider": { "@type": "MedicalBusiness", "name": "Bella Rose Medical Center" },
      "areaServed": { "@type": "City", "name": "Dubai" },
      "description": "Korean and Allergan Botox administered by board-certified dermatologists. Results last 4-6 months. DHA-licensed clinic in Al Mamzar Dubai.",
      "offers": {
        "@type": "AggregateOffer",
        "lowPrice": "650",
        "highPrice": "1200",
        "priceCurrency": "AED",
        "offerCount": "2"
      }
    },
    {
      "@context": "https://schema.org",
      "@type": "Service",
      "serviceType": "Dental Implant",
      "provider": { "@type": "MedicalBusiness", "name": "Bella Rose Medical Center" },
      "areaServed": { "@type": "City", "name": "Dubai" },
      "description": "German and French dental implant systems with warranty. Premium titanium alloy implants placed by specialist oral surgeons in Al Mamzar Dubai.",
      "offers": {
        "@type": "AggregateOffer",
        "lowPrice": "2000",
        "highPrice": "2500",
        "priceCurrency": "AED",
        "offerCount": "2"
      }
    },
    {
      "@context": "https://schema.org",
      "@type": "Service",
      "serviceType": "Laser Hair Removal",
      "provider": { "@type": "MedicalBusiness", "name": "Bella Rose Medical Center" },
      "areaServed": { "@type": "City", "name": "Dubai" },
      "description": "Painless laser hair removal using Candela Max Pro and Deca Again devices with cooling technology. Full body packages available at Bella Rose Al Mamzar Dubai.",
      "offers": {
        "@type": "AggregateOffer",
        "lowPrice": "100",
        "highPrice": "1500",
        "priceCurrency": "AED"
      }
    },
    {
      "@context": "https://schema.org",
      "@type": "Service",
      "serviceType": "CoolSculpting Fat Reduction",
      "provider": { "@type": "MedicalBusiness", "name": "Bella Rose Medical Center" },
      "areaServed": { "@type": "City", "name": "Dubai" },
      "description": "Non-invasive CoolSculpting fat freezing treatment for abdomen, love handles, and double chin. Permanent fat cell elimination at Bella Rose Al Mamzar Dubai.",
      "offers": {
        "@type": "Offer",
        "price": "550",
        "priceCurrency": "AED"
      }
    },
    {
      "@context": "https://schema.org",
      "@type": "Service",
      "serviceType": "PRP Hair Restoration",
      "provider": { "@type": "MedicalBusiness", "name": "Bella Rose Medical Center" },
      "areaServed": { "@type": "City", "name": "Dubai" },
      "description": "Clinically proven PRP (Platelet-Rich Plasma) therapy for hair loss. Also offering Red PRP and Exosome therapy for enhanced results at Bella Rose Al Mamzar Dubai.",
      "offers": {
        "@type": "AggregateOffer",
        "lowPrice": "350",
        "highPrice": "1500",
        "priceCurrency": "AED"
      }
    },

    // === HOW-TO SCHEMAS (AEO: optimized for featured snippets) ===
    {
      "@context": "https://schema.org",
      "@type": "HowTo",
      "name": "How to Get a Hollywood Smile at Bella Rose Dubai",
      "description": "Step-by-step guide to getting a Hollywood Smile with porcelain veneers at Bella Rose Medical Center in Dubai.",
      "totalTime": "P7D",
      "estimatedCost": {
        "@type": "MonetaryAmount",
        "currency": "AED",
        "value": "3000"
      },
      "step": [
        {
          "@type": "HowToStep",
          "name": "Book Free Consultation",
          "text": "Schedule a free consultation at Bella Rose Medical Center via WhatsApp (+971 52 517 1078) or phone (+971 4 296 4442). Our dental specialist will examine your teeth and discuss your smile goals.",
          "position": 1
        },
        {
          "@type": "HowToStep",
          "name": "Digital Smile Design",
          "text": "Your dentist creates a digital preview of your new smile using 3D scanning technology. You'll see the expected results before any treatment begins.",
          "position": 2
        },
        {
          "@type": "HowToStep",
          "name": "Tooth Preparation",
          "text": "Minimal tooth preparation is performed under local anesthesia. Temporary veneers are placed while your custom porcelain veneers are crafted in the lab.",
          "position": 3
        },
        {
          "@type": "HowToStep",
          "name": "Veneer Placement",
          "text": "Your custom 3D or 6D porcelain veneers are bonded to your teeth. The dentist ensures perfect fit, color match, and natural appearance.",
          "position": 4
        },
        {
          "@type": "HowToStep",
          "name": "Follow-Up Care",
          "text": "Return for a follow-up visit to ensure comfort and satisfaction. Your Hollywood Smile can last 10-15 years with proper care.",
          "position": 5
        }
      ]
    },
    {
      "@context": "https://schema.org",
      "@type": "HowTo",
      "name": "How to Get Botox Treatment at Bella Rose Dubai",
      "description": "Complete guide to Botox treatment at Bella Rose Medical Center — from consultation to results.",
      "totalTime": "PT30M",
      "estimatedCost": {
        "@type": "MonetaryAmount",
        "currency": "AED",
        "value": "650"
      },
      "step": [
        {
          "@type": "HowToStep",
          "name": "Consultation with Dermatologist",
          "text": "Meet with a board-certified dermatologist (Dr. Radwa, Dr. Fatima Salah, or Dr. Mohamed Khadour) for a free assessment of your concerns and treatment goals.",
          "position": 1
        },
        {
          "@type": "HowToStep",
          "name": "Choose Botox Type",
          "text": "Select between Korean Botox (from 650 AED) or premium Allergan Botox (from 1,200 AED) based on your doctor's recommendation.",
          "position": 2
        },
        {
          "@type": "HowToStep",
          "name": "Treatment Session",
          "text": "The procedure takes 10-15 minutes with minimal discomfort. Precise micro-injections target specific muscles for natural-looking results.",
          "position": 3
        },
        {
          "@type": "HowToStep",
          "name": "See Results",
          "text": "Results appear within 3-7 days and last 4-6 months. Schedule your next session at Bella Rose to maintain your refreshed appearance.",
          "position": 4
        }
      ]
    },
    {
      "@context": "https://schema.org",
      "@type": "HowTo",
      "name": "How Laser Hair Removal Works at Bella Rose Dubai",
      "description": "Step-by-step guide to painless laser hair removal using Candela Max Pro at Bella Rose Medical Center.",
      "totalTime": "PT60M",
      "estimatedCost": {
        "@type": "MonetaryAmount",
        "currency": "AED",
        "value": "300"
      },
      "step": [
        {
          "@type": "HowToStep",
          "name": "Initial Assessment",
          "text": "A specialist evaluates your skin type and hair color to customize laser settings for optimal results using Candela Max Pro or Deca Again devices.",
          "position": 1
        },
        {
          "@type": "HowToStep",
          "name": "Treatment Session",
          "text": "The laser targets hair follicles with built-in cooling technology for virtually painless treatment. Session duration varies by area (15-60 minutes).",
          "position": 2
        },
        {
          "@type": "HowToStep",
          "name": "Complete 6-8 Sessions",
          "text": "Attend 6-8 sessions spaced 4-6 weeks apart for optimal permanent hair reduction. Save 30% with a 6-session full body package at 1,500 AED.",
          "position": 3
        }
      ]
    },

    // === BREADCRUMB SCHEMA (AEO: navigation structure) ===
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      "itemListElement": [
        { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://bellaroseclinics.ae" },
        { "@type": "ListItem", "position": 2, "name": "Services", "item": "https://bellaroseclinics.ae/#services" },
        { "@type": "ListItem", "position": 3, "name": "Our Doctors", "item": "https://bellaroseclinics.ae/#doctors" },
        { "@type": "ListItem", "position": 4, "name": "FAQ", "item": "https://bellaroseclinics.ae/#faq" },
        { "@type": "ListItem", "position": 5, "name": "Contact", "item": "https://bellaroseclinics.ae/#contact" },
        { "@type": "ListItem", "position": 6, "name": "Why Bella Rose", "item": "https://bellaroseclinics.ae/why-bella-rose" },
        { "@type": "ListItem", "position": 7, "name": "Blog", "item": "https://bellaroseclinics.ae/blog" },
        { "@type": "ListItem", "position": 8, "name": "Offers", "item": "https://bellaroseclinics.ae/offers" }
      ]
    },

    // === ENHANCED ORGANIZATION SCHEMA (AIO: AI training data) ===
    {
      "@context": "https://schema.org",
      "@type": "MedicalOrganization",
      "name": "Bella Rose Medical Center",
      "alternateName": ["مركز بيلا روز الطبي", "Bella Rose Clinic Dubai", "Bella Rose Dental Center"],
      "url": "https://bellaroseclinics.ae",
      "logo": "https://bellaroseclinics.ae/logo.png",
      "telephone": "+97142964442",
      "email": "info@bellaroseclinics.ae",
      "foundingDate": "2017",
      "numberOfEmployees": { "@type": "QuantitativeValue", "value": 30 },
      "slogan": "With You Until The Best Of You",
      "knowsAbout": [
        "Hollywood Smile", "Dental Veneers", "Teeth Whitening", "Dental Implants",
        "Botox", "Dermal Fillers", "Exosome Therapy", "PRP Hair Treatment",
        "Laser Hair Removal", "CoolSculpting", "Weight Loss Injections",
        "HydraFacial", "Endolift", "IV Drip Therapy"
      ],
      "medicalSpecialty": [
        "Dentistry", "Cosmetic Dentistry", "Dermatology", "Aesthetic Medicine",
        "Bariatric Medicine", "Laser Medicine", "Regenerative Medicine"
      ],
      "address": {
        "@type": "PostalAddress",
        "streetAddress": "Muhammad Bin Rashid Charity Association Building, Cairo Street",
        "addressLocality": "Al Mamzar",
        "addressRegion": "Dubai",
        "postalCode": "00000",
        "addressCountry": "AE"
      },
      "geo": {
        "@type": "GeoCoordinates",
        "latitude": 25.2949675,
        "longitude": 55.3511937
      },
      "openingHoursSpecification": {
        "@type": "OpeningHoursSpecification",
        "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"],
        "opens": "10:00",
        "closes": "23:00"
      },
      "aggregateRating": {
        "@type": "AggregateRating",
        "ratingValue": "4.0",
        "bestRating": "5",
        "worstRating": "1",
        "reviewCount": "1138",
        "ratingCount": "1138"
      },
      "hasOfferCatalog": {
        "@type": "OfferCatalog",
        "name": "Bella Rose Medical Services",
        "itemListElement": [
          { "@type": "OfferCatalog", "name": "Dental Services", "description": "Hollywood Smile, Veneers, Implants, Whitening, Root Canal, Crowns, Cleaning" },
          { "@type": "OfferCatalog", "name": "Dermatology & Aesthetics", "description": "Botox, Fillers, Exosome, PRP Skin, Mesotherapy, Thread Lift" },
          { "@type": "OfferCatalog", "name": "Weight Loss", "description": "Appetite Suppressant Injections, CoolSculpting, Body Contouring" },
          { "@type": "OfferCatalog", "name": "Laser Treatments", "description": "Hair Removal, Skin Rejuvenation, Tattoo Removal using Candela Max Pro" },
          { "@type": "OfferCatalog", "name": "Hair Restoration", "description": "PRP, Red PRP, Exosome Hair Therapy, GFC Treatment" },
          { "@type": "OfferCatalog", "name": "Facial Treatments", "description": "HydraFacial, Hollywood Facial, Carbon Peel, Chemical Peel" }
        ]
      },
      "paymentAccepted": "Cash, Visa, Mastercard, American Express, Apple Pay, Samsung Pay, Tabby, Tamara",
      "currenciesAccepted": "AED",
      "priceRange": "$$"
    }
  ];

  return (
    <>
      {schemas.map((schema, i) => (
        <script
          key={`seo-schema-${i}`}
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }}
        />
      ))}
    </>
  );
}
